import type { PropertyCalendarDeposit, PropertyCalendarDepositEvent } from '@/api/client'
import { DEFAULT_PROPERTY_TIME_ZONE, localDate } from '@/components/cleaning/presentation'

/**
 * Presentation rules for the calendar's deposits layer.
 *
 * The API owns every instant and the order of the events. Nothing here makes
 * up a time, moves an event or drops one from the timeline: the functions pick
 * which events get a marker on the grid, fold runs of repeated renewals into one
 * row, and format what the API returned in the property's own zone.
 */

/** The deposit is held by us or by Uplisting; either way these are the event names an operator reads. */
const EVENT_LABELS: Record<string, string> = {
  hold_due: 'Hold due',
  placed: 'Hold placed',
  renewed: 'Hold renewed',
  inspection_ends: 'Inspection ends',
  expected_lapse: 'Hold expected to lapse',
  backstop_release: 'Backstop release',
  captured: 'Charged',
  released: 'Released',
  refunded: 'Refunded',
  disputed: 'Disputed',
}

/** Events where money moved or was contested: worth the eye stopping on. */
const ATTENTION_TYPES = new Set(['captured', 'refunded', 'disputed'])

/**
 * "Hold renewed", or - for a type this build has not met - the API's own name
 * with the underscores taken out, so a new event still shows up and says what
 * it is called.
 */
export function depositEventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type.replace(/_/g, ' ')
}

/** Whether money moved or was contested: the calendar draws these in amber. */
export function isAttentionEvent(type: string): boolean {
  return ATTENTION_TYPES.has(type)
}

function zonedFormat(timeZone: string, options: Intl.DateTimeFormatOptions) {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, ...options })
  } catch {
    return new Intl.DateTimeFormat('en-US', { timeZone: DEFAULT_PROPERTY_TIME_ZONE, ...options })
  }
}

/** "Oct 15, 2:15 PM" in the property's zone; the raw value when it is not a date. */
export function formatDepositInstant(iso: string, timeZone: string): string {
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return iso
  return zonedFormat(timeZone, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(ms)
}

/** "Eastern Time" for `America/New_York`, so the sheet can say which clock its times are on. */
export function timeZoneName(timeZone: string): string {
  const part = zonedFormat(timeZone, { timeZoneName: 'longGeneric' })
    .formatToParts(Date.now())
    .find(candidate => candidate.type === 'timeZoneName')
  return part?.value ?? timeZone
}

/**
 * The timeline dot's colours: mint for the routine steps, amber where money
 * moved or was contested. Recorded is a filled dot and planned an outlined one,
 * so the two differ by shape and not by colour alone.
 */
export function depositDotClasses(type: string, planned: boolean): string {
  const attention = isAttentionEvent(type)
  if (planned) {
    return attention ? 'border-amber bg-transparent' : 'border-mint bg-transparent'
  }
  return attention ? 'border-amber bg-amber' : 'border-mint bg-mint'
}

/**
 * One marker on the grid: a deposit event on the property-local day it falls on.
 */
export interface DepositMarker {
  /** Stable and unique across deposits, for React. */
  key: string
  reservationId: string
  type: string
  at: string
  planned: boolean
  /** Property-local `YYYY-MM-DD`. */
  day: string
}

/**
 * The events that get a marker on the grid.
 *
 * Every event type is drawn except `renewed`, because a long stay's hold is
 * renewed over and over (the sweep re-authorizes it as often as hourly when a
 * hold keeps lapsing; Naples's long stay has more than forty) and a marker for
 * each would bury the dates an operator looks for. Of the RECORDED renewals only
 * the latest keeps a marker - "when was this last renewed" is the question the
 * grid can answer - while a PLANNED renewal always does. The deposit timeline in
 * the stay sheet lists every one of them.
 */
export function gridMarkerEvents(
  events: PropertyCalendarDepositEvent[]
): PropertyCalendarDepositEvent[] {
  let lastRecordedRenewal = -1
  events.forEach((event, index) => {
    if (event.type === 'renewed' && !event.planned) lastRecordedRenewal = index
  })
  return events.filter(
    (event, index) => event.type !== 'renewed' || event.planned || index === lastRecordedRenewal
  )
}

/**
 * The markers for the given deposits, each on its property-local day and
 * restricted to the days `inDay` accepts (the visible month: a deposit's
 * timeline reaches outside it). Oldest first within a day, then in the API's
 * order, so a cell reads the way the timeline does.
 */
export function buildDepositMarkers(
  deposits: PropertyCalendarDeposit[],
  timeZone: string,
  inDay: (day: string) => boolean
): DepositMarker[] {
  const markers: DepositMarker[] = []
  for (const deposit of deposits) {
    for (const [index, event] of gridMarkerEvents(deposit.events).entries()) {
      const day = localDate(event.at, timeZone)
      if (!day || !inDay(day)) continue
      markers.push({
        key: `${deposit.reservation_id}:${event.type}:${event.at}:${index}`,
        reservationId: deposit.reservation_id,
        type: event.type,
        at: event.at,
        planned: event.planned,
        day,
      })
    }
  }
  return markers.sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
}

export function groupMarkersByDay(markers: DepositMarker[]): Map<string, DepositMarker[]> {
  const byDay = new Map<string, DepositMarker[]>()
  for (const marker of markers) {
    const list = byDay.get(marker.day)
    if (list) list.push(marker)
    else byDay.set(marker.day, [marker])
  }
  return byDay
}

/** A run this long or longer of one repeated event is folded into a single row. */
export const COLLAPSE_RUN_LENGTH = 3

export type TimelineRow =
  | { kind: 'event'; key: string; event: PropertyCalendarDepositEvent }
  | {
      kind: 'run'
      key: string
      type: string
      planned: boolean
      /** The run's events, in the API's order: at least `COLLAPSE_RUN_LENGTH`. */
      events: PropertyCalendarDepositEvent[]
    }

/**
 * The timeline as rows, in the order the API returned the events.
 *
 * Only the order the API gave is used: nothing is sorted, so an event that
 * arrives out of sequence (a refund recorded before the hold was placed) is
 * shown where it came. A run of `COLLAPSE_RUN_LENGTH` or more consecutive events
 * of the same type and the same planned/recorded kind becomes one `run` row
 * that the sheet can expand to every event; shorter runs stay as they are.
 */
export function buildTimelineRows(events: PropertyCalendarDepositEvent[]): TimelineRow[] {
  const rows: TimelineRow[] = []
  let start = 0
  while (start < events.length) {
    let end = start + 1
    while (
      end < events.length &&
      events[end].type === events[start].type &&
      events[end].planned === events[start].planned
    ) {
      end++
    }
    const run = events.slice(start, end)
    if (run.length >= COLLAPSE_RUN_LENGTH) {
      rows.push({
        kind: 'run',
        key: `run:${start}`,
        type: events[start].type,
        planned: events[start].planned,
        events: run,
      })
    } else {
      run.forEach((event, offset) =>
        rows.push({ kind: 'event', key: `event:${start + offset}`, event })
      )
    }
    start = end
  }
  return rows
}
